import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Addchart, CameraOutlined, CreateNewFolder, UploadFile } from "@mui/icons-material";
import { Button, Divider, ListItemIcon, ListItemText, MenuItem, MenuList, useTheme } from "@mui/material";
import { useSnackbar } from "notistack";

import analytics from "fond/analytics";
import { selectCurrentAccount, selectFolderById } from "fond/api";
import { useFeatureFlag } from "fond/featureFlags";
import { useCreatePermissionCheck } from "fond/hooks/useCreatePermissionCheck";
import { useEntityUtil } from "fond/hooks/useEntityUtil";
import { NewFolder } from "fond/projects";
import NewCityPlanner from "fond/projects/CityPlanner/NewCityPlanner";
import { Project, ProjectSubtype, ProjectType, ResourceEntity } from "fond/types";
import { NewItemSource } from "fond/types/analytics";
import { useAppSelector } from "fond/utils/hooks";
import { Modal, SubscriptionErrorMessage } from "fond/widgets";
import BetaChip from "fond/widgets/BetaChip";

import { NewProjectDialog } from "./NewProjectDialog";

interface RouteParams {
  folderId: string;
}

type NewItemListProps = {
  /**
   * An item was clicked
   */
  onItemClick?: () => void;
  showDividers?: boolean;
  /**
   * A source for analytics event tracking
   */
  source: NewItemSource;
};

const NewItemList: React.FC<NewItemListProps> = ({ onItemClick, showDividers = false, source }: NewItemListProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [currentModal, setCurrentModal] = useState<null | "folder" | "cityPlanner" | "subscriptionError" | "newProject">(null);
  const { folderId } = useParams<keyof RouteParams>();
  const {
    canAddFolder,
    canCreateProject,
    canImportReport,
    canWriteCityPlannerProject,
    canWriteISGDesignProjects,
    canWritePlannerProjects,
    canWritePlannerExtendedProjects,
    canWriteNBNProjects,
    canWriteMuPowerProjects,
    hasNBNModule,
    hasMuPowerModule,
  } = useCreatePermissionCheck();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { getEntityIcon, getEntityLabel } = useEntityUtil();

  const { value: showImportReportOption } = useFeatureFlag("import_report");
  const { value: showNBNAutoDesignOption } = useFeatureFlag("nbn_auto_design");

  const folder = useAppSelector((state) => (folderId ? selectFolderById(state, folderId) : undefined));
  const [projectTypeInfo, setProjectTypeInfo] = useState<{ type: ProjectType; subType?: ProjectSubtype }>({ type: "planner" });

  /**
   * Helper to render entity icon and label for menu items
   */
  const renderEntityIconAndLabel = (entity: Partial<ResourceEntity>, labelLength: "short" | "long" = "long") => (
    <>
      <ListItemIcon>{getEntityIcon(entity, { fontSize: "small" })}</ListItemIcon>
      <ListItemText>{getEntityLabel(entity, labelLength)}</ListItemText>
    </>
  );

  /**
   * Project create success handler
   * @param project - the newly created project
   */
  const onProjectCreateComplete = (project: Project) => {
    analytics.track("New Item.Project.Created project", { projectId: project.ID, projectType: project.Type, source });
    navigate(`/project/${project.ID}/`);
  };

  /**
   * Request project creation
   */
  const createProject = async (projectType: ProjectType, subType?: ProjectSubtype): Promise<void> => {
    if (!currentAccount) {
      enqueueSnackbar("There was an issue creating this project. Please try again.", { variant: "error" });
      return;
    }

    setProjectTypeInfo({ type: projectType, subType });
    setCurrentModal("newProject");
  };

  const onCreateCityPlannerClick = async (): Promise<void> => {
    analytics.track("new-item.market-lens.clicked", { source });
    setCurrentModal("cityPlanner");
  };

  const onCreateFolderClick = () => {
    analytics.track("new-item.folder.clicked", { source });
    setCurrentModal("folder");
  };

  const closeCurrentModal = () => setCurrentModal(null);

  useEffect(() => {
    if (currentModal !== null && onItemClick) {
      onItemClick();
    }
  }, [currentModal, onItemClick]);

  const mainBlue = theme.palette.primary.main;

  const divider = useMemo(() => (showDividers ? <Divider /> : null), [showDividers]);

  if (!canAddFolder && !canCreateProject) {
    return null;
  }

  return (
    <>
      <MenuList sx={{ color: mainBlue }} component="nav">
        <MenuItem
          className="create-folder"
          data-testid="create-folder-option"
          aria-disabled={!canAddFolder}
          disabled={!canAddFolder}
          onClick={onCreateFolderClick}
        >
          <ListItemIcon>
            <CreateNewFolder fontSize="small" sx={{ color: mainBlue }} />
          </ListItemIcon>
          <ListItemText>Folder</ListItemText>
        </MenuItem>

        {divider}

        <MenuItem
          className="create-planner"
          data-testid="create-planner-option"
          aria-disabled={!canCreateProject}
          disabled={!canCreateProject}
          onClick={() => {
            if (canWritePlannerProjects) {
              createProject("planner");
            } else {
              setCurrentModal("subscriptionError");
            }
          }}
        >
          {renderEntityIconAndLabel({ EntityType: "project", Type: "planner" }, "long")}
        </MenuItem>
        {/*
          Currently disabled until further notice
          {showDesignProjectOption && (
            <MenuItem
              className="create-design"
              data-testid="create-small-design-option"
              aria-disabled={!canCreateProject}
              disabled={!canCreateProject}
              onClick={() => createAndOpenProject("design", "irby_small_batch")}
            >
              <ListItemIcon>
                <ImageAspectRatio fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Small Design</ListItemText>
            </MenuItem>
          )}
         */}
        {canWritePlannerExtendedProjects && (
          <MenuItem
            className="create-planner-extended"
            data-testid="create-planner-extended"
            aria-disabled={!canCreateProject}
            disabled={!canCreateProject}
            onClick={() => createProject("planner", "planner_extended")}
          >
            {renderEntityIconAndLabel({ EntityType: "project", Type: "planner", Subtype: "planner_extended" }, "long")}
            <BetaChip label="BETA" variant="filled" size="small" color="secondary" sx={{ ml: 1 }} />
          </MenuItem>
        )}
        <MenuItem
          className="create-collaboration"
          data-testid="create-collaboration-option"
          aria-disabled={!canCreateProject}
          disabled={!canCreateProject}
          onClick={() => createProject("collaboration")}
        >
          {renderEntityIconAndLabel({ EntityType: "project", Type: "collaboration" }, "long")}
        </MenuItem>

        {canWriteCityPlannerProject && divider}
        {canWriteCityPlannerProject && (
          <MenuItem key="city-planner" className="create-city-planner" data-testid="create-city-planner-option" onClick={onCreateCityPlannerClick}>
            <ListItemIcon>
              <CameraOutlined fontSize="small" sx={{ color: mainBlue }} />
            </ListItemIcon>
            <ListItemText>Market Lens Project</ListItemText>
          </MenuItem>
        )}

        {hasNBNModule && [
          <MenuItem
            key="nbn-cost-to-serve"
            className="create-nbn-cost-to-serve"
            data-testid="create-nbn-cost-to-serve-option"
            aria-disabled={!canCreateProject}
            disabled={!canCreateProject}
            onClick={() => {
              if (canWriteNBNProjects) {
                createProject("design", "nbn_cost_to_serve");
              } else {
                setCurrentModal("subscriptionError");
              }
            }}
          >
            {renderEntityIconAndLabel({ EntityType: "project", Type: "design", Subtype: "nbn_cost_to_serve" }, "short")}
          </MenuItem>,
          showNBNAutoDesignOption && (
            <MenuItem
              key="nbn-idd-lite"
              className="create-nbn-idd-lite"
              data-testid="create-nbn-idd-lite-option"
              aria-disabled={!canCreateProject}
              disabled={!canCreateProject}
              onClick={() => {
                if (canWriteNBNProjects) {
                  createProject("design", "nbn_idd_lite");
                } else {
                  setCurrentModal("subscriptionError");
                }
              }}
            >
              {renderEntityIconAndLabel({ EntityType: "project", Type: "design", Subtype: "nbn_idd_lite" }, "short")}
            </MenuItem>
          ),
        ]}

        {hasMuPowerModule && (
          <MenuItem
            className="create-mu-power"
            data-testid="create-mu-power-option"
            aria-disabled={!canCreateProject}
            disabled={!canCreateProject}
            onClick={() => {
              if (canWriteMuPowerProjects) {
                createProject("design", "mu_power");
              } else {
                setCurrentModal("subscriptionError");
              }
            }}
          >
            {renderEntityIconAndLabel({ EntityType: "project", Type: "design", Subtype: "mu_power" }, "short")}
          </MenuItem>
        )}

        {canWriteISGDesignProjects && (
          <MenuItem
            className="create-isg-design"
            data-testid="create-isg-design-option"
            onClick={() => {
              createProject("design", "isg");
            }}
          >
            {renderEntityIconAndLabel({ EntityType: "project", Type: "design", Subtype: "isg" }, "long")}
          </MenuItem>
        )}

        {divider}

        <MenuItem
          className="create-report"
          data-testid="create-report-option"
          onClick={() => {
            analytics.track("New Item.Report.Create report menu item clicked", { source });
            navigate({ pathname: "/reports/create", search: folderId && new URLSearchParams({ folderId }).toString() });
          }}
        >
          <ListItemIcon>
            <Addchart fontSize="small" sx={{ color: mainBlue }} />
          </ListItemIcon>
          <ListItemText>Report</ListItemText>
        </MenuItem>

        {showImportReportOption && canImportReport && (
          <MenuItem
            className="import-report"
            data-testid="import-report-option"
            onClick={() => {
              analytics.track("New Item.Import Report.Import report menu item clicked", { source });
              navigate({ pathname: "/reports/import", search: folderId && new URLSearchParams({ folderId }).toString() });
            }}
          >
            <ListItemIcon>
              <UploadFile fontSize="small" sx={{ color: mainBlue }} />
            </ListItemIcon>
            <ListItemText>Import Report</ListItemText>
          </MenuItem>
        )}
      </MenuList>

      {currentModal === "newProject" && currentAccount && (
        <NewProjectDialog
          parentId={folderId}
          onClose={closeCurrentModal}
          projectType={projectTypeInfo.type}
          projectSubtype={projectTypeInfo.subType}
          onComplete={onProjectCreateComplete}
        />
      )}

      {currentModal === "folder" && <NewFolder onClose={closeCurrentModal} parentId={folderId} />}
      {currentModal === "cityPlanner" && currentAccount && <NewCityPlanner onClose={closeCurrentModal} parentFolder={folder} />}
      {currentModal === "subscriptionError" && (
        <Modal
          open
          data-testid="planner-project-error-modal"
          variant="secondary"
          header="Oops..."
          content={<SubscriptionErrorMessage />}
          actions={
            <Button data-testid="back-button" color="primary" onClick={closeCurrentModal}>
              Back
            </Button>
          }
        />
      )}
    </>
  );
};

export default NewItemList;
